import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const ReorderPoint = () => {
  // State variables for Reorder Point calculation
  const [averageDailyUsage, setAverageDailyUsage] = useState(0); // Average daily usage
  const [leadTime, setLeadTime] = useState(0); // Lead time in days
  const [safetyStock, setSafetyStock] = useState(0); // Safety stock
  const [reorderPoint, setReorderPoint] = useState(0); // Calculated reorder point

  // Event handlers to update state values
  const handleAverageDailyUsageChange = (e) => setAverageDailyUsage(parseFloat(e.target.value) || 0);
  const handleLeadTimeChange = (e) => setLeadTime(parseFloat(e.target.value) || 0);
  const handleSafetyStockChange = (e) => setSafetyStock(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate reorder point when any input changes
  useEffect(() => {
    const point = (averageDailyUsage * leadTime) + safetyStock;
    setReorderPoint(point);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [averageDailyUsage, leadTime, safetyStock]);

  return (
    <Layout title="Reorder Point Calculator" path="reorder-point">
      <section className="section-padding" id="reorder-point-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Reorder Point Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Reorder Point Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Average Daily Usage Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="averageDailyUsage" className="form-label">
                        Average Daily Usage (units)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Average Daily Usage:</strong> The average number of units used per day during a given period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="averageDailyUsage"
                        placeholder="Enter average daily usage"
                        value={averageDailyUsage || ""}
                        onChange={handleAverageDailyUsageChange}
                      />
                    </div>

                    {/* Lead Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="leadTime" className="form-label">
                        Lead Time (days)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Lead Time:</strong> The number of days it takes for new stock to be delivered once an order is placed."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="leadTime"
                        placeholder="Enter lead time in days"
                        value={leadTime || ""}
                        onChange={handleLeadTimeChange}
                      />
                    </div>

                    {/* Safety Stock Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="safetyStock" className="form-label">
                        Safety Stock (units)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Safety Stock:</strong> The buffer stock kept to prevent stockouts during unexpected demand or delays."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="safetyStock"
                        placeholder="Enter safety stock"
                        value={safetyStock || ""}
                        onChange={handleSafetyStockChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Reorder Point:{" "}
                        <span className="fw-bold ms-3">
                          {reorderPoint === 0 ? "0" : reorderPoint.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} units
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Reorder Point Calculator
                </h3>

                {/* What is a Reorder Point Calculator? */}
                <h4 className="mt-3 mb-3">What is a Reorder Point Calculator?</h4>
                <p>
                  The **Reorder Point Calculator** helps businesses determine the inventory level at which a new order should be placed to avoid stockouts. It considers daily usage, lead time, and safety stock to ensure smooth operations.
                </p>

                {/* Why Use a Reorder Point Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Reorder Point Calculator?</h4>
                <p>
                  This tool is essential for maintaining optimal inventory levels, minimizing the risk of running out of stock, and ensuring timely replenishment.
                </p>

                {/* Benefits of Using a Reorder Point Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Reorder Point Calculator</h4>
                <ul>
                  <li><strong>Ensures Timely Restocking:</strong> Helps prevent stockouts by determining when to place orders.</li>
                  <li><strong>Optimizes Inventory Levels:</strong> Avoids overstocking and reduces carrying costs.</li>
                  <li><strong>Supports Efficient Supply Chain Management:</strong> Helps streamline inventory planning and procurement processes.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Reorder Point Calculator?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Retail Businesses:</strong> To maintain adequate stock levels and avoid losing sales.</li>
                  <li><strong>Wholesalers:</strong> To manage bulk inventory and ensure timely replenishment.</li>
                  <li><strong>Supply Chain Professionals:</strong> To optimize procurement processes and reduce delays.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is a good reorder point?</h4>
                <p>
                  A good reorder point varies by industry and product demand. It should cover the lead time demand plus a buffer (safety stock) to account for variability.
                </p>

                <h4 className="mt-4 mb-3">How can I reduce my reorder point?</h4>
                <p>
                  To reduce your reorder point, decrease lead times by working with faster suppliers or improve demand forecasting to minimize variability.
                </p>

                <h4 className="mt-4 mb-3">Why is safety stock included in the formula?</h4>
                <p>
                  Safety stock is included to account for demand fluctuations or unexpected delays in supply, ensuring continuity in operations even under uncertain conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReorderPoint;
